import React from 'react';
import { ColumnDef, ExpandedState, Row } from '@tanstack/react-table';

import {
  DATA_GRID_DEFAULT_COLUMN_WIDTH,
  DATA_GRID_ROW_HEADER_COLUMN_ID,
  DATA_GRID_ROW_HEADER_WIDTH,
} from './constants';
import {
  DataGridAlign,
  DataGridColumn,
  DataGridColumnGroup,
  DataGridColumnNode,
  DataGridExpansionConfig,
  DataGridRowHeaderConfig,
} from './types';

export const getDataGridJustifyClass = (align?: DataGridAlign): string => {
  if (align === 'center') {
    return 'justify-center';
  }
  if (align === 'right') {
    return 'justify-end';
  }
  return 'justify-start';
};

export const getDataGridTextAlignClass = (align?: DataGridAlign): string => {
  if (align === 'center') {
    return 'text-center';
  }
  if (align === 'right') {
    return 'text-right';
  }
  return 'text-left';
};

export const isDataGridColumnGroup = <TRow,>(
  node: DataGridColumnNode<TRow>
): node is DataGridColumnGroup<TRow> => 'columns' in node;

export const getDataGridColumnMinWidthStyle = (
  width: number
): React.CSSProperties => ({ minWidth: width });

export interface DataGridColumnModel<TRow> {
  columnDefs: ColumnDef<TRow>[];
  leafColumnsById: Map<string, DataGridColumn<TRow>>;
  groupColumnsById: Map<string, DataGridColumnGroup<TRow>>;
}

export const buildDataGridColumnModel = <TRow,>(
  columns: DataGridColumnNode<TRow>[],
  rowHeaderConfig: DataGridRowHeaderConfig<TRow>
): DataGridColumnModel<TRow> => {
  const leafColumnsById = new Map<string, DataGridColumn<TRow>>();
  const groupColumnsById = new Map<string, DataGridColumnGroup<TRow>>();

  const toColumnDef = (node: DataGridColumnNode<TRow>): ColumnDef<TRow> => {
    if (isDataGridColumnGroup(node)) {
      groupColumnsById.set(node.id, node);
      return { id: node.id, columns: node.columns.map(toColumnDef) };
    }
    leafColumnsById.set(node.id, node);
    return { id: node.id, size: node.width ?? DATA_GRID_DEFAULT_COLUMN_WIDTH };
  };

  const rowHeaderColumnDef: ColumnDef<TRow> = {
    id: DATA_GRID_ROW_HEADER_COLUMN_ID,
    size: rowHeaderConfig.width ?? DATA_GRID_ROW_HEADER_WIDTH,
  };

  return {
    columnDefs: [rowHeaderColumnDef, ...columns.map(toColumnDef)],
    leafColumnsById,
    groupColumnsById,
  };
};

export const toDataGridExpandedState = (
  expandedIds: string[] | undefined
): ExpandedState =>
  Object.fromEntries(
    (expandedIds ?? []).map((rowId): [string, boolean] => [rowId, true])
  );

export const toDataGridExpandedIds = (state: ExpandedState): string[] => {
  if (state === true) {
    return [];
  }
  return Object.keys(state).filter((rowId) => state[rowId]);
};

export const getDataGridSearchEmptyDescription = (searchTerm: string): string =>
  `Your search “${searchTerm}” did not match any result. Please try again.`;

export const buildDataGridRowCanExpand =
  <TRow,>(
    isExpansionEnabled: boolean,
    expansionConfig: DataGridExpansionConfig | undefined
  ) =>
  (row: Row<TRow>): boolean => {
    if (!isExpansionEnabled || row.subRows.length === 0) {
      return false;
    }
    return expansionConfig?.isRowExpandable?.(row.id) ?? true;
  };
